import { db } from "../../config/firebase";
import { collection, addDoc, getDoc, query, where, limit, getDocs, doc, deleteDoc, updateDoc, orderBy, arrayUnion, arrayRemove, Timestamp, onSnapshot, serverTimestamp, setDoc, startAfter } from 'firebase/firestore'
import { httpsCallable } from "firebase/functions";
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { storage, functions as firebaseFunctions, auth, firebaseFunctionsBaseUrl } from "../../config/firebase";
import axios from 'axios';

const requireUserId = (userId) => {
    if (!userId) {
        throw new Error('User not found');
    }
    return userId;
};

const toMillis = (value) => {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value.seconds === 'number') return value.seconds * 1000;
    if (typeof value === 'number') return value < 1e12 ? value * 1000 : value;
    if (typeof value === 'string') {
        const t = new Date(value).getTime();
        return Number.isNaN(t) ? null : t;
    }
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
};

const normalizeLionDoc = (docSnap, meetingId) => {
    const raw = docSnap.data() || {};

    let filesData = {};
    if (Array.isArray(raw.files) && raw.files.length > 0) {
        filesData = raw.files[0] || {};
    } else if (raw.files && typeof raw.files === 'object') {
        filesData = raw.files;
    }

    const type = (filesData.type || raw.type || '').toString().toLowerCase();

    return {
        id: docSnap.id,
        type,
        title: filesData.title || raw.title || '(Başlıksız)',
        context: raw.context || filesData.context || '',
        date: raw.date ?? filesData.date ?? null,
        users: raw.users || [],
        userId: filesData.userId || raw.userId || null,
        lionId: filesData.lionId || raw.lionId || docSnap.id,
        meetingId: filesData.meetingId || raw.meetingId || meetingId,
        state: filesData.state ?? raw.state ?? true,
        status: filesData.status ?? raw.status ?? true,
        files: filesData,
    };
};

const createMeeting = async (meetingData, userId) => {
    const colRef = collection(db, 'meetings');
    const docRef = await addDoc(colRef, {
        ...meetingData,

        activityOrder: 0,
        activityType: "standard",
        adminUsers: [userId],
        amount: 0.01,
        blackList: [],
        btobmeetings: [],
        businessId: "",

        // Önce formdan gelen varsa onu al, yoksa default
        category: meetingData.category ?? "0",
        meetingTemplate: meetingData.meetingTemplate ?? "99",
        meetingType: meetingData.meetingType ?? "standard",
        tags: meetingData.tags ?? [],

        changingUsers: [],
        coverPhoto: "",
        createDate: Timestamp.fromDate(new Date()),
        createMeetingUserId: userId,
        explore: false,
        files: [],
        follower: [],
        image: "",
        invitations: [],
        isFree: true,
        isFront: false,
        isMatching: false,
        isactivity: meetingData.isactivity || false,
        istob: false,
        isbtobsession: false,
        issubactivity: false,
        issubmeeting: false,
        location: meetingData.location ?? "",
        matchingSurveyUsers: [],
        meetingBusinessState: false,
        moderatorUsers: [],
        modules: [],
        nonUserInvited: [],
        premium: true,
        private: true,
        sessionbreakduaration: 0,
        sessioncount: 0,
        sessiondeskcount: 0,
        sessionduration: 0,
        stableUsers: [],
        state: true,
        status: "0",
        titleSearch: (meetingData.title || '').toString().toLocaleLowerCase('tr-TR'),
        subactivitys: [],
        submeetings: [],
        topmeetingid: "",
        url: meetingData.url ?? "",
        urlAttendee: meetingData.urlAttendee ?? "",
        urlHost: meetingData.urlHost ?? "",
        urlLocation: meetingData.urlLocation ?? "",
        urlWeb: meetingData.urlWeb ?? "",
        userLimit: 0,
        userReq: [],
        userId: userId,
        users: [userId],
    });

    // Belgeye meetingId ekle
    await updateDoc(doc(db, 'meetings', docRef.id), { meetingId: docRef.id });

    // Kullanıcı profiline meeting id eklenemese bile meeting oluşturma akışını bozma.
    try {
        await updateDoc(doc(db, 'users', userId), {
            meetings: arrayUnion(docRef.id)
        });
    } catch (error) {
        console.warn('users/{uid}.meetings update skipped:', error);
    }

    // Firestore verisini oku ve meetingId ile birlikte döndür
    const docSnap = await getDoc(docRef);
    return { ...docSnap.data(), meetingId: docRef.id };
};


const getMeetings = async (userId) => {
    const resolvedUserId = requireUserId(userId);
    const colRef = collection(db, 'meetings');
    const q = query(
        colRef,
        where("users", "array-contains", resolvedUserId),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    // "Tüm Toplantılarım" ekranında sadece toplantıları göster
    dizi = dizi.filter((item) => item?.isactivity !== true);
    const getSortTime = (meeting) =>
        toMillis(meeting?.endDate) ?? toMillis(meeting?.startDate) ?? 0;
    return dizi.sort((a, b) => getSortTime(b) - getSortTime(a));
}

const getMeetingsPage = async ({ userId, pageSize = 15, cursor = null, searchTerm = '' }) => {
    const resolvedUserId = requireUserId(userId);
    const colRef = collection(db, 'meetings');
    const normalizedSearch = (searchTerm || '').trim().toLocaleLowerCase('tr-TR');
    const isSearchMode = normalizedSearch.length > 0;

    let q;
    if (isSearchMode) {
        q = query(
            colRef,
            where("users", "array-contains", resolvedUserId),
            where("titleSearch", ">=", normalizedSearch),
            where("titleSearch", "<=", normalizedSearch + '\uf8ff'),
            orderBy('titleSearch'),
            limit(pageSize),
        );
        if (cursor) {
            q = query(
                colRef,
                where("users", "array-contains", resolvedUserId),
                where("titleSearch", ">=", normalizedSearch),
                where("titleSearch", "<=", normalizedSearch + '\uf8ff'),
                orderBy('titleSearch'),
                startAfter(cursor),
                limit(pageSize),
            );
        }
    } else {
        q = query(
            colRef,
            where("users", "array-contains", resolvedUserId),
            orderBy('endDate', 'desc'),
            limit(pageSize),
        );
        if (cursor) {
            const cursorTs = typeof cursor === 'number'
                ? Timestamp.fromMillis(cursor)
                : cursor;
            q = query(
                colRef,
                where("users", "array-contains", resolvedUserId),
                orderBy('endDate', 'desc'),
                startAfter(cursorTs),
                limit(pageSize),
            );
        }
    }

    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((docSnap) => {
        dizi.push({ ...docSnap.data(), id: docSnap.id })
    });

    dizi = dizi.filter((item) => item?.isactivity !== true);

    const lastItem = dizi[dizi.length - 1];
    const nextCursor = isSearchMode
        ? (lastItem?.titleSearch || '').toString().toLocaleLowerCase('tr-TR') || null
        : (toMillis(lastItem?.endDate) ?? toMillis(lastItem?.startDate) ?? null);

    return {
        items: dizi,
        hasMore: querySnapshot.size === pageSize,
        nextCursor,
    };
}

const getMeetingsByMonth = async (userId, year, monthIndex) => {
    const resolvedUserId = requireUserId(userId);
    const colRef = collection(db, 'meetings');
    const start = Timestamp.fromDate(new Date(year, monthIndex, 1, 0, 0, 0, 0));
    const end = Timestamp.fromDate(new Date(year, monthIndex + 1, 0, 23, 59, 59, 999));
    const q = query(
        colRef,
        where("users", "array-contains", resolvedUserId),
        where("startDate", ">=", start),
        where("startDate", "<=", end),
        orderBy("startDate", "asc"),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const getFutureMeetings = async (userId) => {
    const resolvedUserId = requireUserId(userId);
    const colRef = collection(db, 'meetings');
    const now = Timestamp.fromDate(new Date());
    const q = query(
        colRef,
        where("users", "array-contains", resolvedUserId),
        where("endDate", ">=", now),
        orderBy("endDate", "asc"),
    );

    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        const item = { ...doc.data(), id: doc.id };
        dizi.push(item);
    });

    dizi.sort((a, b) => {
        const aMs = toMillis(a?.startDate) ?? toMillis(a?.endDate) ?? 0;
        const bMs = toMillis(b?.startDate) ?? toMillis(b?.endDate) ?? 0;
        return aMs - bMs;
    });

    return dizi;
}

const getPastMeetings = async (userId) => {
    const resolvedUserId = requireUserId(userId);
    const colRef = collection(db, 'meetings');

    // Şu anki zamanı Firestore Timestamp olarak al
    const currentDate = Timestamp.fromDate(new Date());

    const q = query(
        colRef,
        where("users", "array-contains", resolvedUserId),
        // Sadece startDate şu anki tarihten büyük veya eşit olanları getir
        where("startDate", "<=", currentDate),
        // Gelecekteki toplantıları yakın tarihli olanlar önce gelecek şekilde sırala
        orderBy('startDate', 'desc'),
    );

    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const getMeetingsFront = async () => {
    const colRef = collection(db, 'meetings');
    const q = query(
        colRef,
        where("isactivity", "==", true),
        where("explore", "==", true),
        where("isFront", "==", true),
        where("state", "==", true),
        //where("businessId", "==", "j6HB1d7Ua41NTFhvH4lw"),
        where('endDate', '>=', new Date()),
        orderBy('endDate'),
        //where('endDate', '>=', new Date())
        //where("startDate", ">=", new Date()),
        //limit(3) // İlk 3 belgeyi alacak şekilde sınırlandırma
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });

    // JavaScript ile activityOrder'a göre sıralama
    dizi.sort((a, b) => a.activityOrder - b.activityOrder);

    // İlk 6 elemanı almak için slice kullanıyoruz
    const firstItems = dizi.slice(0, 3);



    return firstItems;
}

const getMeetingsPast = async () => {
    const colRef = collection(db, 'meetings');
    const q = query(
        colRef,
        where("isactivity", "==", true),
        where("explore", "==", true),
        //where("businessId", "==", "j6HB1d7Ua41NTFhvH4lw"),
        where('endDate', '<=', new Date()),
        where("state", "==", true),
        orderBy('endDate', 'desc'),
        //where('endDate', '>=', new Date())
        //where("startDate", ">=", new Date()),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const subscribeLionNotes = (meetingId, onChange) => {
    const typeKeyMap = {
        lastweek: 'lastWeek',
        issues: 'issues',
        opportunities: 'opportunities',
        nextweek: 'nextWeek',
    };

    const lionsCol = collection(db, 'meetings', meetingId, 'lions');

    return onSnapshot(lionsCol, (snap) => {
        const buckets = {
            lastWeek: [],
            issues: [],
            opportunities: [],
            nextWeek: [],
        };

        snap.docs.forEach((docSnap) => {
            const normalized = normalizeLionDoc(docSnap, meetingId);
            const sectionKey = typeKeyMap[normalized.type];
            if (sectionKey && buckets[sectionKey]) {
                buckets[sectionKey].push(normalized);
            }
        });

        const sortByDateDesc = (a, b) => {
            const aMs = a?.date?.toMillis?.() ?? (a?.date?.seconds ? a.date.seconds * 1000 : 0);
            const bMs = b?.date?.toMillis?.() ?? (b?.date?.seconds ? b.date.seconds * 1000 : 0);
            return bMs - aMs;
        };

        const nextBuckets = {
            lastWeek: buckets.lastWeek.sort(sortByDateDesc),
            issues: buckets.issues.sort(sortByDateDesc),
            opportunities: buckets.opportunities.sort(sortByDateDesc),
            nextWeek: buckets.nextWeek.sort(sortByDateDesc),
        };

        if (typeof onChange === 'function') {
            onChange(nextBuckets);
        }
    });
};

const createLionNote = async ({ meetingId, sectionKey, title, details, userId }) => {
    const newLionRef = doc(collection(db, 'meetings', meetingId, 'lions'));

    const typeValueMap = {
        lastWeek: 'lastweek',
        issues: 'issues',
        opportunities: 'opportunities',
        nextWeek: 'nextweek',
    };
    const typeValue = typeValueMap[sectionKey] || String(sectionKey).toLowerCase();

    const trimmedTitle = (title || '').trim();
    const trimmedDetails = (details || '').trim();

    const rootFields = {
        context: trimmedDetails,
        date: serverTimestamp(),
        lionId: newLionRef.id,
        meetingId,
        state: true,
        status: true,
        title: trimmedTitle,
        type: typeValue,
        userId,
        users: [userId],
    };

    const lionData = {
        ...rootFields,
        files: [],
    };

    await setDoc(newLionRef, lionData);

    return { id: newLionRef.id, ...lionData };
};

const getSubMeetings = async (meetingId) => {
    const colRef = collection(db, 'meetings');
    const q = query(
        colRef,
        where("topmeetingid", "==", meetingId),
        where("issubmeeting", "==", true),
        where("isbtob", "==", false),
        where("state", "==", true),
        orderBy('startDate'),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const getMeeting = async (meetingId) => {
    const docRef = doc(db, 'meetings', meetingId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        return null;
    }
    return { ...docSnap.data(), id: docSnap.id };

}

const subscribeMeeting = (meetingId, onChange) => {
    const docRef = doc(db, 'meetings', meetingId);
    return onSnapshot(docRef, (docSnap) => {
        if (docSnap && typeof onChange === 'function') {
            onChange({ ...docSnap.data(), id: docSnap.id });
        }
    });
};


/*
const getMeeting = async (meetingId) => {
    const docRef = doc(db, 'meetings', meetingId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
        return { ...docSnap.data(), id: docSnap.id }
    } else {
        return null;
    }
}
*/

const deleteMeeting = async (id) => {
    const docRef = doc(db, 'meetings', id);
    await deleteDoc(docRef);
    return id
}

const updateMeeting = async (meetingData, id) => {
    const docRef = doc(db, 'meetings', id);
    await updateDoc(docRef, meetingData)
    const docSnap = await getDoc(docRef)
    return { ...docSnap.data(), id: docSnap.id };
}

const updateMeetingRecord = async (meetingData, id, onProgress) => {
    try {
        // Önce dosyayı storage'a yükle
        const file = meetingData.recordFile;

        // Dosya adını benzersiz hale getir
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const timestamp = new Date().getTime();

        // Basit bir UUID benzeri oluştur (tam bir UUID için ek bir kütüphane gerekli olabilir)
        const randomPart = Math.random().toString(36).substring(2, 10);

        // Benzersiz dosya adı oluştur - toplantı ID + zaman damgası + rastgele sayı + uzantı
        const uniqueFileName = `audio_${id}_${timestamp}_${randomPart}.${fileExtension}`;

        // Orijinal dosya adını sakla
        const originalFileName = file.name;

        // Benzersiz dosya adıyla storage referansı oluştur
        const storageRef = ref(storage, `meetings/${id}/record/${uniqueFileName}`);

        // Set up upload task with progress monitoring
        const uploadTask = uploadBytesResumable(storageRef, file);

        // Return a promise that resolves when the upload is complete
        return new Promise((resolve, reject) => {
            // Monitor upload progress
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    // Calculate and report progress
                    const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );

                    // If onProgress callback is provided, call it with the progress
                    if (typeof onProgress === 'function') {
                        onProgress(progress);
                    }
                },
                (error) => {
                    // Handle upload errors
                    console.error('Upload error:', error);
                    reject(error);
                },
                async () => {
                    try {
                        // Get the download URL
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        const now = Timestamp.fromDate(new Date());

                        // Update the Firestore document
                        const docRef = doc(db, 'meetings', id);
                        const updateData = {
                            recordingUrl: downloadURL,
                            meetingrecordurl: downloadURL,
                            meetingrecordurls: arrayUnion(downloadURL),
                            recordingName: originalFileName, // Kullanıcıya gösterilecek orijinal dosya adı
                            recordingFileName: uniqueFileName, // Firebase'de saklanan benzersiz dosya adı
                            recordingUpdateDate: now
                        };

                        await updateDoc(docRef, updateData);

                        // Her yüklenen dosya için meetings/{meetingId}/records altına metadata kaydı oluştur
                        const recordDocRef = doc(collection(db, 'meetings', id, 'records'));
                        await setDoc(recordDocRef, {
                            recordId: recordDocRef.id,
                            meetingId: id,
                            name: originalFileName,
                            fileName: uniqueFileName,
                            url: downloadURL,
                            path: `meetings/${id}/record/${uniqueFileName}`,
                            mimeType: file.type || "",
                            extension: fileExtension || "",
                            sizeBytes: file.size || 0,
                            status: true,
                            state: true,
                            createDate: now,
                            updateDate: now,
                        });

                        const docSnap = await getDoc(docRef);

                        // Resolve the promise with the updated data
                        resolve({
                            ...docSnap.data(),
                            id: docSnap.id,
                            uploadProgress: 100
                        });
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    } catch (error) {
        throw new Error(error.message);
    }
}

const getMeetingUsers = async (meetingId) => {
    const colRef = collection(db, 'users');
    const q = query(
        colRef,
        where('meetings', 'array-contains-any', [meetingId]),
        orderBy('name'),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const getMeetingAgendas = async (meetingId) => {
    const colRef = collection(db, 'meetings', meetingId, 'agenda');
    const q = query(
        colRef,
        where('status', "==", true),
        orderBy('startTime', 'asc'),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const getMeetingNotes = async (meetingId, userId) => {
    const resolvedUserId = requireUserId(userId);
    const colRef = collection(db, 'meetings', meetingId, 'notes');
    const q = query(
        colRef,
        where('users', 'array-contains-any', [resolvedUserId]),
        orderBy('messageDate', 'desc'),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const createMeetingNote = async (noteData, meetingId, userId) => {
    const colRef = collection(db, 'meetings', meetingId, 'notes');
    const docRef = await addDoc(colRef, {
        ...noteData,
        messageDate: noteData?.messageDate || Timestamp.fromDate(new Date()),
        userId: userId,
        users: [userId],

    })
    const docSnap = await getDoc(docRef);
    console.log("docSnap.id: " + docSnap.id);


    const docRefU = doc(db, 'meetings', meetingId, 'notes', docSnap.id);
    await updateDoc(docRefU, { noteId: docSnap.id })

    return { ...docSnap.data(), noteId: docSnap.id };
}


const joinMeeting = async (meetingId, userId) => {
    const docRefUser = doc(db, 'users', userId);

    const docRefMeeting = doc(db, 'meetings', meetingId);


    try {

        await updateDoc(docRefMeeting, {
            users: arrayUnion(userId)
        });

        // Diziye yeni elemanı ekleyin
        await updateDoc(docRefUser, {
            meetings: arrayUnion(meetingId)
        });



        console.log('Diziye yeni eleman başarıyla eklendi.');
    } catch (error) {
        console.error('Diziye eleman eklerken hata oluştu:', error);
        throw error;
    }

    return { status: "true" };
}

const addMeetingUserByEmail = async (meetingId, email) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    if (!normalizedEmail) {
        throw new Error('Geçerli bir e-posta adresi giriniz.');
    }

    const usersColRef = collection(db, 'users');
    const exactEmailQuery = query(usersColRef, where('userEmail', '==', normalizedEmail), limit(1));
    let userSnapshot = await getDocs(exactEmailQuery);

    if (userSnapshot.empty) {
        const rawEmail = (email || '').trim();
        const rawEmailQuery = query(usersColRef, where('userEmail', '==', rawEmail), limit(1));
        userSnapshot = await getDocs(rawEmailQuery);
    }

    if (userSnapshot.empty) {
        throw new Error('USER_NOT_FOUND');
    }

    const foundUserDoc = userSnapshot.docs[0];
    const foundUserId = foundUserDoc.id;

    const docRefUser = doc(db, 'users', foundUserId);
    const docRefMeeting = doc(db, 'meetings', meetingId);

    await updateDoc(docRefMeeting, {
        users: arrayUnion(foundUserId),
    });

    await updateDoc(docRefUser, {
        meetings: arrayUnion(meetingId),
    });

    return { status: "true", userId: foundUserId };
}

const removeMeetingUser = async (meetingId, userId) => {
    if (!meetingId || !userId) {
        throw new Error('Geçersiz kullanıcı veya toplantı.');
    }

    const docRefUser = doc(db, 'users', userId);
    const docRefMeeting = doc(db, 'meetings', meetingId);

    await updateDoc(docRefMeeting, {
        users: arrayRemove(userId),
        adminUsers: arrayRemove(userId),
    });

    await updateDoc(docRefUser, {
        meetings: arrayRemove(meetingId),
    });

    return { status: "true", userId };
}


const getMeetingAnnouncements = async (meetingId) => {
    const colRef = collection(db, 'meetings', meetingId, 'announcement');
    const q = query(
        colRef,
        where('status', "==", true),
        orderBy('messageDate', 'desc'),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}


const getMeetingAIDetail = async (meetingId) => {
    const colRef = collection(db, 'assemblyAI');
    const q = query(
        colRef,
        where("meetingId", "==", meetingId),
        orderBy("timestamp", "desc"),
        limit(1)
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi[0];
}

const getMeetingSpeakers = async (meetingId) => {


    const colRef = collection(db, 'assemblyAI');
    const q = query(
        colRef,
        where("meetingId", "==", meetingId),
        orderBy("timestamp", "desc"),
        limit(1)
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });

    if (!dizi.length) {
        return [];
    }

    const assemblyAIdocId = dizi[0].id;
    if (!assemblyAIdocId) {
        return [];
    }

    const assemblyAIcolRef = collection(db, 'assemblyAI', assemblyAIdocId, 'speakers');
    const assemblyAIq = query(
        assemblyAIcolRef,
        orderBy('start', 'asc'),
    );
    const assemblyAIquerySnapshot = await getDocs(assemblyAIq);
    let assemblyAIdizi = [];
    assemblyAIquerySnapshot.forEach((doc) => {
        assemblyAIdizi.push({ ...doc.data(), id: doc.id })
    });

    return assemblyAIdizi;
}

const getMeetingSpeechAnalysis = async (meetingId) => {
    const colRef = collection(db, 'assemblyAI');
    const q = query(
        colRef,
        where("meetingId", "==", meetingId),
        orderBy("timestamp", "desc"),
        limit(1)
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });

    if (!dizi.length) {
        return null;
    }

    const assemblyAIdocId = dizi[0].id;
    if (!assemblyAIdocId) {
        return null;
    }

    const analysisColRef = collection(db, 'assemblyAI', assemblyAIdocId, 'speech_analysis');
    const currentAnalysisDoc = await getDoc(doc(db, 'assemblyAI', assemblyAIdocId, 'speech_analysis', 'current'));
    if (currentAnalysisDoc.exists()) {
        return { ...currentAnalysisDoc.data(), id: currentAnalysisDoc.id };
    }

    const analysisQuerySnapshot = await getDocs(query(analysisColRef, orderBy('createdAt', 'desc')));
    let analysisList = [];
    analysisQuerySnapshot.forEach((docSnap) => {
        analysisList.push({ ...docSnap.data(), id: docSnap.id })
    });
    return analysisList[0] || null;
}

const triggerMeetingSpeechAnalysis = async ({ meetingId, documentId = null }) => {
    const token = await auth.currentUser?.getIdToken?.();
    if (!token) {
        throw new Error('User not authenticated');
    }

    const response = await fetch(
        `${firebaseFunctionsBaseUrl}/triggerMeetingSpeechAnalysis`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ meetingId, documentId }),
        }
    );

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data?.error || 'Speech analysis trigger failed');
    }
    return data;
}

const getMeetingDecisions = async (meetingId) => {
    const colRef = collection(db, 'meetings', meetingId, 'decision');
    const q = query(
        colRef,
        where('status', "==", true),
        orderBy('date', 'asc'),
    );
    const querySnapshot = await getDocs(q);
    let dizi = [];
    querySnapshot.forEach((doc) => {
        dizi.push({ ...doc.data(), id: doc.id })
    });
    return dizi;
}

const createGoogleMeetLink = async ({ startAt, endAt, title, description, attendees = [] }) => {
    const callable = httpsCallable(firebaseFunctions, 'createGoogleMeetLink');
    const response = await callable({ startAt, endAt, title, description, attendees });
    return response.data;
}

const createZoomMeetingLink = async ({ startAt, endAt, title, description, attendees = [] }) => {
    const callable = httpsCallable(firebaseFunctions, 'createZoomMeetingLink');
    const response = await callable({ startAt, endAt, title, description, attendees });
    return response.data;
}



const meetingService = {
    createMeeting,
    createGoogleMeetLink,
    createZoomMeetingLink,
    getMeetings,
    getMeetingsPage,
    getMeetingsByMonth,
    deleteMeeting,
    updateMeeting,
    getMeeting,
    subscribeMeeting,
    getSubMeetings,
    getMeetingUsers,
    getMeetingNotes,
    createMeetingNote,
    joinMeeting,
    addMeetingUserByEmail,
    removeMeetingUser,
    getMeetingsPast,
    getMeetingsFront,
    getFutureMeetings,
    getPastMeetings,
    getMeetingAgendas,
    getMeetingAnnouncements,
    updateMeetingRecord,
    getMeetingAIDetail,
    getMeetingSpeakers,
    getMeetingSpeechAnalysis,
    triggerMeetingSpeechAnalysis,
    getMeetingDecisions,
    subscribeLionNotes,
    createLionNote
}

export default meetingService
